import { useEffect } from "react";
import { useRouter } from "next/router";
import { formatDistanceToNow } from "date-fns";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { A } from "@components/Anchor";
import { Layout } from "@components/Layout/index";
import {
  getAllDocumentsByType,
  getJobPosting,
  getModalSettings,
  getGlobalConfig,
} from "@lib/api/calls";
import { trackEvent } from "@lib/track-event";
import { oxford } from "@services/format/string";
import TextSection from "@components/sections/TextSection";
import styles from "@components/Typography/typography.module.css";
import careerStyles from "./careers.module.css";

function computeSubject(title) {
  const router = useRouter();
  const { slug } = router.query;

  let subject = encodeURIComponent(`[Careers: ${title}]`);

  if (slug === "general-applications") {
    subject = encodeURIComponent("[DEPARTMENT] - General Application");
  }

  if (slug === "general-applications-internships") {
    subject = encodeURIComponent("[DEPARTMENT] - General Application (Internship)");
  }

  return subject;
}

export default function Career({
  preview,
  title,
  location,
  createdAt,
  email,
  seo,
  posting,
  slug,
  config,
  subscribed,
  modal,
}) {
  const { t } = useTranslation("careers");
  useEffect(() => {
    trackEvent("view_page", {
      pageTitle: title,
      pageCategory: "Information Page",
      pageLocation: window.location.href,
      pagePath: window.location.pathname,
    });
  }, []);

  const subject = computeSubject(title);
  return (
    <Layout
      config={config}
      title={title}
      seo={seo}
      subscribed={subscribed}
      isPreview={preview}
      pageFound={slug !== null}
      modal={modal}
    >
      <div className={careerStyles.backButtonWrapper}>
        <Link href={`/careers`} passHref>
          <A>&larr; {t("all-careers-link")}</A>
        </Link>
      </div>
      <div className={careerStyles.headingWrapper}>
        <h1 className={styles.heading1}>{title}</h1>
      </div>
      <div className={careerStyles.textSectionWrapper}>
        {posting && <TextSection text={posting} />}
      </div>
      {email && (
        <div className={careerStyles.applyWrapper}>
          <A isButton href={`mailto:${email}?subject=${subject}`}>
            {t("apply-button")}
          </A>
        </div>
      )}
      <div className={careerStyles.dateSection}>
        <p className={styles.paragraph}>Hiring in {oxford(location, "and")}</p>
        <p className={styles.paragraph}>
          Posted {createdAt && formatDistanceToNow(new Date(createdAt))} ago
        </p>
      </div>
    </Layout>
  );
}

export async function getStaticPaths() {
  // Return a list of possible value for id
  const allJobs = await getAllDocumentsByType("job");
  const paths = allJobs?.map((job) => ({
    params: {
      slug: job.slug,
    },
  }));
  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps({ params, preview = false, locale }) {
  const jobPage = params?.slug ? await getJobPosting(params.slug, preview) : null;
  const modal = await getModalSettings(locale, preview);
  const config = await getGlobalConfig(locale, preview);

  const seo = {
    seoTitle: jobPage?.meta?.seoTitle || `${jobPage?.title} | Kotn`,
    seoDescription:
      jobPage?.meta?.seoDescription ||
      "We're looking for the best of the curious, talented, and passionate to come join us.",
    openGraphImage: jobPage?.meta?.openGraphImage || null,
    twitterCard: jobPage?.meta?.seoTwitterCardStyle || "summary_large_image",
  };

  return {
    props: {
      preview,
      createdAt: jobPage?.createdAt || null,
      location: jobPage?.location || null,
      title: jobPage?.title || null,
      email: jobPage?.email || null,
      seo,
      posting: jobPage?.blocks || null,
      slug: jobPage?.slug || null,
      modal,
      config,
      ...(await serverSideTranslations(locale, ["common", "navbar", "footer", "careers", "cart"])),
    },
    revalidate: 1800,
  };
}
